import logo from "@/assets/logo.png";
import logoLight from "@/assets/logo-light.png";
import mark from "@/assets/mark.png";
import markLight from "@/assets/mark-light.png";

import { cn } from "@/utils/utils";

/**
 * Which artwork suits the surface behind the lockup.
 *
 * `auto` is the usual answer and the default: the navy lockup on a light theme,
 * the white one on a dark theme, swapped by the same `dark:` variant everything
 * else on the page uses. It is the default because the alternative was each
 * caller remembering to render both and hide one, and the sign-in, reset and
 * change-password screens all forgot — the navy lockup sat on a near-black card,
 * barely legible, while the app shell three files away did it correctly.
 *
 * `ink` and `light` pin the artwork regardless of theme, for a surface whose own
 * colour does not follow the theme: the member and credential cards are dark
 * artwork in both, so their lockup is always the white one.
 */
type Tone = "auto" | "ink" | "light";

/** Rendered twice for `auto`, so the theme picks rather than the caller. */
function Artwork({
  tone,
  dark,
  light,
  className,
  ...rest
}: {
  tone: Tone;
  dark: string;
  light: string;
  className?: string;
} & React.ImgHTMLAttributes<HTMLImageElement>) {
  if (tone !== "auto") {
    return <img src={tone === "light" ? light : dark} className={className} {...rest} />;
  }
  return (
    <>
      <img src={dark} className={cn(className, "dark:hidden")} {...rest} />
      <img src={light} className={cn(className, "hidden dark:block")} {...rest} />
    </>
  );
}

/** The S&R lockup. */
export function Logo({ tone = "auto", className }: { tone?: Tone; className?: string }) {
  return (
    <Artwork
      tone={tone}
      dark={logo}
      light={logoLight}
      alt="S&R Membership Shopping"
      width={560}
      height={261}
      className={cn("h-auto w-36", className)}
    />
  );
}

/** The S glyph alone, for tight spaces like the sidebar rail. */
export function Mark({ tone = "auto", className }: { tone?: Tone; className?: string }) {
  return (
    <Artwork
      tone={tone}
      dark={mark}
      light={markLight}
      alt=""
      aria-hidden
      width={96}
      height={110}
      className={cn("h-6 w-auto", className)}
    />
  );
}
